import { useRef } from 'react';
import { useSelector } from 'react-redux';

import type { Product } from '@/domains/productDiscovery/algolia/interfaces/product';
import type {
  Element,
  productsImpressionTrackingPageType,
} from '@/domains/productDiscovery/Listings/hooks/useProductsImpressionTracking/interfaces';
import { productsByRowCalculator } from '@/domains/productDiscovery/Listings/hooks/useProductsImpressionTracking/utils/productsByRowCalculator/productsByRowCalculator';
import { useLayout } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useLayout';
import type { WithSponsoredProduct } from '@/domains/productDiscovery/Listings/sponsoredProducts/interfaces/product';
import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import { handleSponsoredProductsImpressionsTracking } from '@/productDiscovery/Listings/hooks/useProductsImpressionTracking/utils/handleSponsoredProductsImpressionsTracking/handleSponsoredProductsImpressionsTracking';

import type { SponsoredProduct } from '../../sponsoredProducts/interfaces/sponsoredProduct';
import { selectSponsoredProductsItems } from '../../sponsoredProducts/selectors/sponsoredProducts';
import { useCurrentPage } from '../useCurrentPage';
import { itemsViewedCalculator } from './utils/itemsViewedCaculator/itemsViewedCaculator';
import { sendGtmEvent } from './utils/sendGtmEvent';

export interface ViewedProduct {
  position: number;
  product: Product | (Product & WithSponsoredProduct);
}

export const useProductsImpressionTracking = (
  pageType: productsImpressionTrackingPageType,
  promotedProductModelId?: number,
): {
  trackProductAndElement: (
    item: Product | (Product & WithSponsoredProduct) | Element,
    index?: number,
  ) => void;
} => {
  const currentPage = useCurrentPage();
  const layout = useLayout();
  const inspirationalProductCardsEnabled = layout === 'inspirational';

  const viewedProducts = useRef<ViewedProduct[]>([]);
  const elements = useRef<Element[]>([]);

  const sponsoredProductsConfig = useSelector<
    SpartacuxProductDiscoveryDomainState,
    SponsoredProduct[] | undefined
  >(selectSponsoredProductsItems);

  const trackProductAndElement = async (
    item: Product | (Product & WithSponsoredProduct) | Element,
    index?: number,
  ) => {
    const productsByRow = productsByRowCalculator(
      pageType === 'gooddeals_listing',
      inspirationalProductCardsEnabled,
    );

    if ('type' in item) {
      elements.current.push(item);
    } else {
      viewedProducts.current.push({
        position: (index as number) + 1,
        product: item,
      });
    }

    const itemsViewedNumber = itemsViewedCalculator(elements, viewedProducts, {
      promotedProductModelId,
    });

    if (
      itemsViewedNumber > 1 &&
      itemsViewedNumber % (2 * productsByRow) === 0
    ) {
      sendGtmEvent(viewedProducts, pageType);

      const viewedProductsForSPTracking = [...viewedProducts.current];

      viewedProducts.current = [];
      elements.current = [];

      handleSponsoredProductsImpressionsTracking(
        viewedProductsForSPTracking,
        pageType,
        currentPage,
        sponsoredProductsConfig,
      );
    }
  };

  return {
    trackProductAndElement,
  };
};
